import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { FaTimes, FaCommentAlt, FaCommentSlash, FaPlay, FaPause, FaExpand } from "react-icons/fa";
import { getCookie } from "../../commonServices/cookieService";
import LiveClassChat from "./LiveClassChat";
import aboutimg from "../../Image/insight_small_logo.png"
import "./youtube.css";

const ChatOverlay = React.memo(({ isClosing, handleCloseChat, streamObject, handleAnimationEnd }) => (
    <div
        className={`chat-overlay ${isClosing ? 'chat-closing' : 'chat-opening'}`}
        onAnimationEnd={handleAnimationEnd}
    >
        <div style={{ height: '100%', width: '100%' }}>
            <LiveClassChat setIsChatOpen={handleCloseChat} stream={streamObject} />
        </div>
    </div>
));

const SpeedControl = ({ speed, onChange }) => (
    <div className="speed-container">
        <label className="control-label" htmlFor="speed-control">Speed:</label>
        <select
            value={speed}
            onChange={(e) => onChange(e.target.value)}
            id="speed-control"
            style={{ backgroundColor: 'black', color: 'white', border: '1px solid #333', borderRadius: '4px', padding: '2px', fontSize: '14px', cursor: 'pointer' }}
        >
            <option value="0.25">0.25x</option>
            <option value="0.5">0.5x</option>
            <option value="1">Normal</option>
            <option value="1.5">1.5x</option>
            <option value="2">2x</option>
        </select>
    </div>
);

const YoutubeCustomVP = ({ selectedClass, videoId = "LZSetBILN8A", stream, onClose }) => {
    const playerRef = useRef(null);
    const [player, setPlayer] = useState(null);
    const [duration, setDuration] = useState(18000);
    const [currentTime, setCurrentTime] = useState(0);
    const [volume, setVolume] = useState(100);
    const [speed, setSpeed] = useState("1");
    // eslint-disable-next-line no-unused-vars
    const [isFullscreen, setIsFullscreen] = useState(() => {
        if (typeof document !== 'undefined') {
            return !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement);
        }
        return false;
    });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [watermark, setWatermark] = useState(false);
    const [showInitialHeader, setShowInitialHeader] = useState(true); // Track first 2 seconds

    // --- NEW: Responsive State for Chat ---
    const [isMobile, setIsMobile] = useState(true);
    const [isTablet, setIsTablet] = useState(false);
    // ------------------------------------

    const playerInitializedRef = useRef(false);
    const seekingLock = useRef(false);

    // --- NEW: Detect Screen Size ---
    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 768); // Mobile threshold
            setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
        };

        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);

        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);
    // ------------------------------

    const handleReplay = () => {
        // 1. Drop the "Curtain" immediately to hide any YouTube glitches
        setIsLoading(true);

        // 2. Update UI state
        setIsPlaying(true);

        // 3. Reset video position and play
        if (player) {
            player.seekTo(0, true); // true = allow seek ahead
            player.playVideo();
        }

        // Note: Your onStateChange listener will automatically 
        // set isLoading(false) once the video actually hits 'PLAYING'
    }

    const formatTime = (seconds) => {
        if (!seconds) return "0:00";
        seconds = Math.floor(seconds);
        const min = Math.floor(seconds / 60);
        const sec = seconds % 60;
        return `${min}:${sec.toString().padStart(2, "0")}`;
    };

    let timer;

    const playVideo = () => {
        clearTimeout(timer);
        player?.playVideo();
        timer = setTimeout(() => setIsPlaying(true), 100);
    };

    const pauseVideo = () => {
        clearTimeout(timer);
        timer = setTimeout(() => player?.pauseVideo(), 100);
        setIsPlaying(false);
    };

    const seekVideo = (percent) => {
        if (!player || !duration) return;
        const time = (percent / 100) * duration;
        setCurrentTime(time);
        player.seekTo(time, true);
    };

    const setPlayerVolume = (val) => {
        setVolume(val);
        player?.setVolume(parseInt(val));
    };

    const setPlayerSpeed = useCallback((val) => {
        const strVal = String(val); // Ensure string for select value matching
        setSpeed(strVal);
        if (player && typeof player.setPlaybackRate === 'function') {
            player.setPlaybackRate(parseFloat(strVal));
        }
    }, [player]);

    useEffect(() => {
        setWatermark([
            getCookie("email") || "testing",
            getCookie("contact"),
            getCookie("name"),
            getCookie("deviceId")
        ]);
    }, []);

    // 1. Initialize YouTube API
    useEffect(() => {
        let internalPlayer = null
        const startSequence = async () => {
            try {
                if (!window.YT) {
                    await new Promise((resolve) => {
                        const tag = document.createElement("script");
                        tag.src = "https://www.youtube.com/iframe_api";
                        tag.onload = () => {
                            window.YT.ready(resolve);
                        };
                        document.head.appendChild(tag);
                    });
                } else if (window.YT && window.YT.ready) {
                    await new Promise((resolve) => window.YT.ready(resolve));
                }

                if (playerRef.current && !playerInitializedRef.current) {
                    internalPlayer = new window.YT.Player(playerRef.current, {
                        videoId: videoId,
                        width: "100%",
                        height: "100%",
                        playerVars: {
                            'enablejsapi': 1,
                            origin: window.location.origin,
                            host: 'https://www.youtube.com',
                            controls: 0,
                            rel: 0,
                            modestbranding: 1,
                            disablekb: 1,
                            iv_load_policy: 3,
                            autoplay: 1,
                            autohide: 1,
                            showinfo: 0,
                            fs: 0,
                            cc_load_policy: 0,
                            playsinline: 1,
                        },
                        events: {
                            onReady: (e) => {
                                setPlayer(e.target);
                                const isLive = selectedClass?.Status === 1;
                                const realDuration = e.target.getDuration();
                                setDuration(isLive ? Math.max(realDuration, 18000) : realDuration);
                                e.target.playVideo();
                                setIsLoading(false);
                                playerInitializedRef.current = true;
                            },
                            onStateChange: (e) => {
                                // Sync local state with player state
                                if (e.data === window.YT.PlayerState.PLAYING) {
                                    setIsPlaying(true);
                                    setIsLoading(false);
                                    // Show black header for first 2 seconds
                                    setShowInitialHeader(true);
                                    setTimeout(() => {
                                        setShowInitialHeader(false);
                                    }, 5000);
                                } else if (e.data === window.YT.PlayerState.PAUSED) {
                                    setIsPlaying(false);
                                } else if (e.data === window.YT.PlayerState.ENDED) {
                                    setIsPlaying(false);
                                    // CHANGE: stopVideo() resets the player more aggressively than pauseVideo()
                                    // This prevents the "Wall of Suggestions" from lingering.
                                    e.target.stopVideo();
                                }
                            },
                            onError: () => setError("Video unavailable or restricted."),
                        },
                    });
                }
            } catch (err) {
                setError("Could not load video player.");
            }
        };
        startSequence();
        // CLEANUP FUNCTION
        return () => {
            if (internalPlayer && internalPlayer.destroy) {
                internalPlayer.destroy();
                playerInitializedRef.current = false;
            }
        };
    }, [videoId]); // Added videoId dependency for initial load safety

    // 2. Handle Video Swapping
    useEffect(() => {
        if (player && playerInitializedRef.current && videoId) {
            console.log("🔄 Switching Video to:", videoId);
            player.loadVideoById(videoId);
        }
    }, [videoId, player]);

    // 3. Progress Tracker & Logic Engine
    useEffect(() => {
        if (!player) return;

        // Increased frequency to 200ms for better precision
        const interval = setInterval(() => {
            try {
                if (typeof player.getCurrentTime === 'function') {
                    const current = player.getCurrentTime();
                    const dur = player.getDuration();
                    const isLive = selectedClass?.Status == 1;

                    // --- FIX: DVR Window Stuck Fix (With Cooldown) ---
                    const playerState = player.getPlayerState();

                    if (!seekingLock.current && isLive && playerState === 3 && (dur - current) > 1800) {
                        console.log("Stuck in DVR tail, jumping to live...");

                        // 1. Lock future jumps
                        seekingLock.current = true;

                        // 2. FORCE UI RESET: We know jumping to live resets speed to 1x, 
                        // so we must tell the Dropdown to match it immediately.
                        setPlayerSpeed(1);

                        // 3. Perform the jump
                        player.seekTo(dur);

                        // 4. Unlock after 5 seconds
                        setTimeout(() => {
                            seekingLock.current = false;
                        }, 5000);
                    }

                    // --- Speed Auto-Reset ---
                    if (isLive && typeof player.getPlaybackRate === 'function') {
                        const rate = player.getPlaybackRate();
                        // Increased threshold to 60s to catch it before YouTube auto-resets
                        if (rate > 1 && (dur - current) < 60) {
                            console.log("Catching up... resetting speed");
                            setPlayerSpeed(1);
                        }
                    }

                    // --- Video Ended Logic ---
                    if (!isLive && dur > 0 && (dur - current) < 0.2) {
                        setIsPlaying(false);
                        player.pauseVideo();
                        player.seekTo(0);
                    } else {
                        setCurrentTime(current);
                        setDuration(isLive ? Math.max(dur, 18000) : dur);
                    }
                }
            } catch (e) {
                // Ignore errors
            }
        }, 200);
        return () => clearInterval(interval);
    }, [player, selectedClass, setPlayerSpeed]);



    // Fullscreen listener
    useEffect(() => {
        const onFsChange = () => {
            setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement));
        };
        // Check immediately in case state is stale
        onFsChange();

        document.addEventListener("fullscreenchange", onFsChange);
        document.addEventListener("webkitfullscreenchange", onFsChange);
        document.addEventListener("mozfullscreenchange", onFsChange);
        document.addEventListener("MSFullscreenChange", onFsChange);
        return () => {
            document.removeEventListener("fullscreenchange", onFsChange);
            document.removeEventListener("webkitfullscreenchange", onFsChange);
            document.removeEventListener("mozfullscreenchange", onFsChange);
            document.removeEventListener("MSFullscreenChange", onFsChange);
        }
    }, []);

    // --- UPDATED: Toggle Instantly (Removed Animation State) ---
    const handleChatToggle = () => {
        setIsChatOpen(!isChatOpen);
    };

    // --- UPDATED: Close Instantly (Removed Animation State) ---
    const handleCloseChat = () => {
        setIsChatOpen(false);
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            const element = document.getElementById('youtubePlayer');
            if (element.requestFullscreen) {
                element.requestFullscreen();
            } else if (element.webkitRequestFullscreen) {
                element.webkitRequestFullscreen(); // Safari
            } else if (element.msRequestFullscreen) {
                element.msRequestFullscreen(); // IE11
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            }
        }
    };

    const handleAnimationEnd = useCallback(() => {
        // Keep strictly for compatibility if passed to children, 
        // but no longer used for self-closing logic
        if (isClosing) {
            setIsChatOpen(false);
            setIsClosing(false);
        }
    }, [isClosing]);

    useEffect(() => {
        // 1. Lock scroll on mount
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        // 2. Unlock scroll on unmount
        return () => {
            document.body.style.overflow = 'unset'; // or 'auto'
            document.documentElement.style.overflow = 'unset';
        };
    }, []);

    // --- UPDATED RESPONSIVE CHAT STYLES LOGIC ---
    const getChatStyles = () => {
        if (isMobile) {
            // Mobile: Positioned at TOP, Smaller Height
            return {
                position: 'absolute',
                top: '75px',      // Position near top (clears header)
                right: '10px',    // Slight margin from right edge
                width: '90vw',
                maxWidth: '250px',
                height: '35vh',   // Reduced height (was 55vh)
                maxHeight: '35vh',
                borderRadius: '12px',
            };
        }
        else if (isTablet) {
            // Tablet: Medium Sidebar Panel
            return {
                position: 'absolute',
                top: '70px', // Sit below header
                right: '20px',
                bottom: '80px', // Sit above controls
                width: '300px', // Medium width for tablet
                borderRadius: '12px 0 0 12px', // Flat left edge looks cleaner next to video
            };
        } else {
            // Laptop: Large Sidebar Panel
            return {
                position: 'absolute',
                top: '70px', // Sit below header
                right: '20px',
                bottom: '80px', // Sit above controls
                width: '420px', // Much wider for laptop
                borderRadius: '12px 0 0 12px', // Flat left edge looks cleaner next to video
            };
        }
    };
    // -------------------------------

    // Create a Portal to attach directly to body, avoiding parent transform/overflow issues
    return ReactDOM.createPortal(
        <div id='youtubePlayer' style={{
            position: 'fixed',
            inset: 0, // Top/Right/Bottom/Left = 0. Cleanest way to cover viewport.
            width: '100%',
            height: '100%',
            zIndex: 2147483647,
            backgroundColor: 'black',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            overscrollBehavior: 'none',
        }}>



            {/* Exit Button */}
            <div className="top-header-bar" style={{ background: showInitialHeader || !isPlaying ? 'black' : 'transparent' }}>
                <div style={{ background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
                    <img
                        src={aboutimg}
                        alt="About"
                        style={{ width: '30px', height: '30px', minWidth: '30px', minHeight: '30px', margin: '0px 0px 5px 10px', objectFit: 'cover', borderRadius: '50%' }}
                    />
                    <h2 className="header-title">INSIGHT IAS ACADEMY, DELHI</h2>
                </div>
                <button
                    className="yt-btn-close"
                    style={{
                        position: 'absolute', top: '20px', right: '20px', zIndex: 100, backgroundColor: '#00000060',
                        color: "white", borderRadius: '100%', width: '24px', height: '24px', display: 'flex', padding: '2px', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s ease-in-out', fontSize: '14px', fontWeight: '700',
                        // Adjust for safe area if needed via margin/top
                        top: 'max(20px, env(safe-area-inset-top))'
                    }}
                    onClick={onClose}
                    title="Close Player"
                >
                    X
                </button>
            </div>

            {/* Error Message */}
            {error ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px', flex: 1, color: 'white' }}>
                    <div style={{ textAlign: 'center' }}>
                        <h3 style={{ color: '#ef4444', marginBottom: '12px', fontSize: '24px', fontWeight: 'bold' }}>No Video Found</h3>
                        <p style={{ color: '#9ca3af' }}>{error}</p>
                    </div>
                </div>
            ) : (
                <>
                    {/* Video Container */}
                    <div id="video-container" className="group" style={{ position: 'relative', width: '100%', height: '100%', backgroundColor: 'black', flex: 1 }}>

                        {/* Loader */}
                        {isLoading && (
                            <div style={{ position: 'absolute', inset: 0, zIndex: 50, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(5px)' }}>
                                <div style={{ width: '48px', height: '48px', border: '4px solid #2563eb', borderTop: '4px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                            </div>
                        )}

                        <div style={{ position: 'absolute', inset: 0, zIndex: 2, cursor: 'default' }}></div>

                        {/* Masks to hide YouTube UI */}
                        <div style={{ position: 'absolute', top: 0, right: 0, width: '250px', height: '80px', zIndex: 5, pointerEvents: 'auto' }}></div>
                        <div style={{ position: 'absolute', bottom: 0, right: 0, width: '150px', height: '60px', zIndex: 5, pointerEvents: 'auto' }}></div>
                        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '30px', zIndex: 5, pointerEvents: 'auto' }}></div>

                        {/* Mask for "More Videos" drawer */}
                        <div style={{ position: 'absolute', bottom: '60px', left: 0, width: '100%', height: '30%', zIndex: 10, pointerEvents: 'none', transition: 'opacity 0.2s', backgroundColor: 'black', opacity: !isPlaying ? 1 : 0 }}></div>

                        {/* End Screen Overlay */}
                        {(!isPlaying && player?.getPlayerState() === 0) && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'absolute', inset: 0, backgroundColor: 'black', color: 'white', zIndex: 20 }}>
                                <div style={{ textAlign: 'center' }}>
                                    <h4 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '12px' }}>Video Ended</h4>
                                    <button
                                        className="yt-btn-replay"
                                        onClick={handleReplay}
                                        style={{ padding: '8px 16px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', transition: 'background-color 0.2s' }}>
                                        Replay
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* The Player */}
                        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                            <div id="player" ref={playerRef} style={{ width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1 }}></div>
                            {watermark[0] && (
                                <>
                                    {/* Helper function to break email pattern detection */}
                                    {(() => {
                                        const disableDetection = (text) => {
                                            if (!text) return "";
                                            return text.toString().split('').join('\u200B');
                                        };

                                        const commonStyle = {
                                            position: 'absolute',
                                            zIndex: 100,
                                            padding: '5px',
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                            borderRadius: '4px',
                                            whiteSpace: 'nowrap',
                                            pointerEvents: 'none',
                                            userSelect: 'none',
                                            WebkitUserSelect: 'none',
                                            color: 'rgba(255,255,255,0.3)',
                                            mixBlendMode: 'luminosity',
                                            fontFamily: 'monospace'
                                        };

                                        return (
                                            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
                                                <div style={{ ...commonStyle, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                                                    {disableDetection(watermark[0])}
                                                </div>
                                                <div style={{ ...commonStyle, top: '10%', left: '10%' }}>
                                                    {disableDetection(watermark[1] || watermark[0])}
                                                </div>
                                                <div style={{ ...commonStyle, top: '10%', right: '10%' }}>
                                                    {disableDetection(watermark[2] || watermark[0])}
                                                </div>
                                                <div style={{ ...commonStyle, bottom: '10%', left: '10%' }}>
                                                    {disableDetection(watermark[3] || watermark[0])}
                                                </div>
                                                <div style={{ ...commonStyle, bottom: '10%', right: '10%' }}>
                                                    {disableDetection(watermark[0])}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </>
                            )}
                        </div>

                        {/* Controls Bar */}
                        <div className="yt-controls-bar">

                            {/* Play Button */}
                            <button
                                className="yt-btn-play"
                                onClick={() => isPlaying ? pauseVideo() : playVideo()}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '90px', padding: '6px 12px', fontSize: '14px', fontWeight: 'bold', border: 'none', borderRadius: '4px', transition: 'transform 0.2s', backgroundColor: 'rgba(255, 255, 255, 0.9)', color: 'black', cursor: 'pointer' }}
                            >
                                {isPlaying ? <FaPause /> : <FaPlay />}
                                <span className="mobile-hidden-text">{isPlaying ? "Pause" : "Play"}</span>
                            </button>

                            {/* Speed Control */}
                            <SpeedControl speed={speed} onChange={setPlayerSpeed} />

                            {/* Volume Control */}
                            <div className="volume-container">
                                <label className="control-label" htmlFor="volume-control">Vol:</label>
                                <input
                                    id="volume-control"
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={volume}
                                    onChange={(e) => setPlayerVolume(e.target.value)}
                                    className="range-custom volume-slider"
                                    style={{
                                        background: `linear-gradient(to right, #ff0000 ${volume}%, rgba(255, 255, 255, 0.3) ${volume}%)`
                                    }}
                                />
                            </div>

                            {/* Seek Bar */}
                            <div className="seek-container">
                                <input
                                    id="seek-bar"
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={duration ? (currentTime / duration) * 100 : 0}
                                    step="0.1"
                                    onChange={(e) => seekVideo(e.target.value)}
                                    className="range-custom"
                                    style={{
                                        background: `linear-gradient(to right, #ff0000 ${duration ? (currentTime / duration) * 100 : 0}%, rgba(255, 255, 255, 0.3) ${duration ? (currentTime / duration) * 100 : 0}%)`
                                    }}
                                />
                            </div>

                            {/* Time Display */}
                            <div className="time-display" style={{ fontFamily: 'monospace', fontSize: '13px', color: '#ddd', minWidth: 'fit-content', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                {formatTime(currentTime)} / {formatTime(duration)}
                            </div>

                            {/* Fullscreen Toggle */}
                            {!isFullscreen && (
                                <button
                                    className="yt-btn-fullscreen"
                                    onClick={toggleFullscreen}
                                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '14px', fontWeight: 'bold', border: 'none', borderRadius: '4px', transition: 'all 0.2s', backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', cursor: 'pointer', marginLeft: 'auto' }}
                                >
                                    <FaExpand />
                                    <span className="mobile-hidden-text">Full</span>
                                </button>
                            )}

                            {/* Chat Toggle */}
                            {selectedClass?.Status !== 2 && (
                                <button
                                    className="yt-btn-chat"
                                    onClick={handleChatToggle}
                                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '14px', fontWeight: '600', border: 'none', borderRadius: '4px', transition: 'all 0.2s', backgroundColor: isChatOpen ? '#e0a800' : '#17a2b8', color: 'white', cursor: 'pointer' }}
                                >
                                    {isChatOpen ? <FaCommentSlash /> : <FaCommentAlt />}
                                    <span className="mobile-hidden-text">{isChatOpen ? "Hide" : "Chat"}</span>
                                </button>
                            )}
                        </div>

                        {/* Chat Overlay - ANIMATION REMOVED */}
                        {isChatOpen && (
                            <div
                                style={{
                                    ...getChatStyles(), // Dynamic styles based on screen size
                                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                    backdropFilter: 'blur(10px)',
                                    zIndex: 105,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                                    overflow: 'hidden',
                                    // Animation property removed
                                }}
                            // onAnimationEnd removed
                            >
                                <div style={{ height: '100%', width: '100%' }}>
                                    <LiveClassChat setIsChatOpen={handleCloseChat} stream={selectedClass || { Sn: videoId, Title: "Live Session" }} />
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>,
        document.body
    );
};

export default YoutubeCustomVP;